// Registry for "simple" educational games (open directly, no settings dialog)
// Each entry maps menu id <-> useEducationalGames hook keys + lazy component import
import MemoryGame from "@/components/MemoryGame";
import MemoryGameSettings from "@/components/MemoryGameSettings";
import MazeGame from "@/components/MazeGame";
import MazeGameSettings from "@/components/MazeGameSettings";
import WhackGame from "@/components/WhackGame";
import WhackGameSettings from "@/components/WhackGameSettings";
import SnakeGame from "@/components/SnakeGame";
import SnakeGameSettings from "@/components/SnakeGameSettings";
import BlockGame from "@/components/BlockGame";
import BlockGameSettings from "@/components/BlockGameSettings";
import SimonGame from "@/components/SimonGame";
import SimonGameSettings from "@/components/SimonGameSettings";
import SnakeLadderGame from "@/components/SnakeLadderGame";
import SnakeLadderSettings from "@/components/SnakeLadderSettings";
import BalonTeman from "@/components/BalonTeman";
import DongBola from "@/components/DongBola";
import MasakBareng from "@/components/MasakBareng";
import TebakGambar from "@/components/TebakGambar";
import MenaraBalok from "@/components/MenaraBalok";
import TangkapBintang from "@/components/TangkapBintang";
import PuzzlePotong from "@/components/PuzzlePotong";
import LombaLari from "@/components/LombaLari";
import Mewarnai from "@/components/Mewarnai";
import GameHitung from "@/components/GameHitung";
import GameMusik from "@/components/GameMusik";
import GameBalap from "@/components/GameBalap";
import LemparBola from "@/components/LemparBola";
import TebakSuara from "@/components/TebakSuara";
import CocokkanWarna from "@/components/CocokkanWarna";
import BelajarHuruf from "@/components/BelajarHuruf";
import BelajarHariBulan from "@/components/BelajarHariBulan";
import TebakBendera from "@/components/TebakBendera";
import SudokuAnak from "@/components/SudokuAnak";
import TebakPola from "@/components/TebakPola";
import BelajarJam from "@/components/BelajarJam";
import CeritaInteraktif from "@/components/CeritaInteraktif";
import TebakProvinsi from "@/components/TebakProvinsi";
import TebakPahlawan from "@/components/TebakPahlawan";

// Convention: each game in useEducationalGames exposes
//   show<Name>Game  : boolean
//   handleOpen<Name>: () => void
//   handleBackFrom<Name>: () => void
export const SIMPLE_GAMES = [
  { id: "balon", name: "BalonTeman", Component: BalonTeman },
  { id: "dongbola", name: "DongBola", Component: DongBola },
  { id: "masak", name: "MasakBareng", Component: MasakBareng },
  { id: "tebak", name: "TebakGambar", Component: TebakGambar },
  { id: "menara", name: "MenaraBalok", Component: MenaraBalok },
  { id: "tangkap", name: "TangkapBintang", Component: TangkapBintang },
  { id: "puzzle", name: "PuzzlePotong", Component: PuzzlePotong },
  { id: "lomba", name: "LombaLari", Component: LombaLari },
  { id: "mewarnai", name: "Mewarnai", Component: Mewarnai },
  { id: "hitung", name: "GameHitung", Component: GameHitung },
  { id: "musik", name: "GameMusik", Component: GameMusik },
  { id: "balap", name: "GameBalap", Component: GameBalap },
  { id: "lempar", name: "LemparBola", Component: LemparBola },
  { id: "suara", name: "TebakSuara", Component: TebakSuara },
  { id: "warna", name: "CocokkanWarna", Component: CocokkanWarna },
  { id: "huruf", name: "BelajarHuruf", Component: BelajarHuruf },
  { id: "haribulan", name: "BelajarHariBulan", Component: BelajarHariBulan },
  { id: "bendera", name: "TebakBendera", Component: TebakBendera },
  { id: "sudoku", name: "SudokuAnak", Component: SudokuAnak },
  { id: "pola", name: "TebakPola", Component: TebakPola },
  { id: "jam", name: "BelajarJam", Component: BelajarJam },
  { id: "cerita", name: "CeritaInteraktif", Component: CeritaInteraktif },
  { id: "provinsi", name: "TebakProvinsi", Component: TebakProvinsi },
  { id: "pahlawan", name: "TebakPahlawan", Component: TebakPahlawan },
];

export const BOARD_MODE_IDS = ["classic", "gomoku", "sos"];

import { Button } from "@/components/ui/button";

// Registry for "settings games" (settings dialog -> game). Each entry is a small dispatcher
// that knows which props each component needs from the useEducationalGames hook.
export const SETTINGS_GAMES = [
  {
    id: "memory",
    showGameKey: "showMemoryGame",
    showSettingsKey: "showMemorySettings",
    renderSettings: (eg) => (
      <>
        <MemoryGameSettings onStartGame={eg.handleStartMemoryGame} />
        <Button onClick={eg.handleBackFromMemory} variant="ghost" className="mt-4 text-white hover:bg-white/10 font-fredoka font-bold">
          Kembali ke Menu Utama
        </Button>
      </>
    ),
    renderGame: (eg) => (
      <MemoryGame
        category={eg.memoryCategory}
        pairCount={eg.memoryPairCount}
        isMultiplayer={eg.memoryMultiplayer}
        player1Name={eg.memoryP1Name}
        player2Name={eg.memoryP2Name}
        playerCount={eg.memoryPlayerCount}
        playerNames={eg.memoryPlayerNames}
        onBackToMenu={eg.handleBackFromMemory}
      />
    ),
  },
  {
    id: "maze",
    showGameKey: "showMazeGame",
    showSettingsKey: "showMazeSettings",
    renderSettings: (eg) => (
      <MazeGameSettings onStartGame={eg.handleStartMazeGame} onBack={eg.handleBackFromMaze} />
    ),
    renderGame: (eg) => (
      <MazeGame characterId={eg.mazeCharacter} onBack={eg.handleBackFromMaze} />
    ),
  },
  {
    id: "whack",
    showGameKey: "showWhackGame",
    showSettingsKey: "showWhackSettings",
    renderSettings: (eg) => (
      <WhackGameSettings onStartGame={eg.handleStartWhackGame} onBack={eg.handleBackFromWhack} />
    ),
    renderGame: (eg) => (
      <WhackGame startLevel={eg.whackStartLevel} onBack={eg.handleBackFromWhack} />
    ),
  },
  {
    id: "snake",
    showGameKey: "showSnakeGame",
    showSettingsKey: "showSnakeSettings",
    renderSettings: (eg) => (
      <SnakeGameSettings onStartGame={eg.handleStartSnakeGame} onBack={eg.handleBackFromSnake} />
    ),
    renderGame: (eg) => (
      <SnakeGame startLevel={eg.snakeStartLevel} onBack={eg.handleBackFromSnake} />
    ),
  },
  {
    id: "block",
    showGameKey: "showBlockGame",
    showSettingsKey: "showBlockSettings",
    renderSettings: (eg) => (
      <BlockGameSettings onStartGame={eg.handleStartBlockGame} onBack={eg.handleBackFromBlock} />
    ),
    renderGame: (eg) => (
      <BlockGame startLevel={eg.blockStartLevel} onBack={eg.handleBackFromBlock} />
    ),
  },
  {
    id: "simon",
    showGameKey: "showSimonGame",
    showSettingsKey: "showSimonSettings",
    renderSettings: (eg) => (
      <SimonGameSettings onStartGame={eg.handleStartSimonGame} onBack={eg.handleBackFromSimon} />
    ),
    renderGame: (eg) => (
      <SimonGame startLevel={eg.simonStartLevel} onBack={eg.handleBackFromSimon} />
    ),
  },
  {
    id: "snakeladder",
    showGameKey: "showSnakeLadderGame",
    showSettingsKey: "showSnakeLadderSettings",
    renderSettings: (eg) => (
      <SnakeLadderSettings onStartGame={eg.handleStartSnakeLadderGame} onBack={eg.handleBackFromSnakeLadder} />
    ),
    renderGame: (eg) => (
      <SnakeLadderGame
        playerCount={eg.snakeLadderPlayerCount}
        playerNames={eg.snakeLadderPlayerNames}
        onBackToMenu={eg.handleBackFromSnakeLadder}
      />
    ),
  },
];

// Maps menu modeId -> useEducationalGames handler name. Used by GameSettings to dispatch opens.
export const OPEN_HANDLER_MAP = {
  memory: "handleOpenMemoryGame",
  maze: "handleOpenMazeGame",
  whack: "handleOpenWhackGame",
  snake: "handleOpenSnakeGame",
  block: "handleOpenBlockGame",
  simon: "handleOpenSimonGame",
  snakeladder: "handleOpenSnakeLadderGame",
  balon: "handleOpenBalonTeman",
  dongbola: "handleOpenDongBola",
  masak: "handleOpenMasakBareng",
  tebak: "handleOpenTebakGambar",
  menara: "handleOpenMenaraBalok",
  tangkap: "handleOpenTangkapBintang",
  puzzle: "handleOpenPuzzlePotong",
  lomba: "handleOpenLombaLari",
  mewarnai: "handleOpenMewarnai",
  hitung: "handleOpenGameHitung",
  musik: "handleOpenGameMusik",
  balap: "handleOpenGameBalap",
  lempar: "handleOpenLemparBola",
  suara: "handleOpenTebakSuara",
  warna: "handleOpenCocokkanWarna",
  huruf: "handleOpenBelajarHuruf",
  haribulan: "handleOpenBelajarHariBulan",
  bendera: "handleOpenTebakBendera",
  sudoku: "handleOpenSudokuAnak",
  pola: "handleOpenTebakPola",
  jam: "handleOpenBelajarJam",
  cerita: "handleOpenCeritaInteraktif",
  provinsi: "handleOpenTebakProvinsi",
  pahlawan: "handleOpenTebakPahlawan",
};
